import { useCallback, useEffect, useState, useRef } from "react";
import { Card, Stack } from "@shopify/polaris";
import { Field } from "react-final-form";
import { useAppMutation } from "../../../../../../../../hooks/index.js";
import { closeModal, openModal } from "../../../../../../../../store/features/modal.js";
import { OPEN_MODAL_TARGETED_PRODUCTS } from "../../../../../../../../constants/modalTypes.js";
import SpecificAttributeCard from "./components/SpecificAttributeCard.js";
import CurrentSelectedProductsCard from "../../../CurrentSelectedProductsCard/CurrentSelectedProductsCard.js";
import TargetedProductsModal from "../../../../../../../modals/TargetedProductsModal/TargetedProductsModal.js";
import {
  SELECT_PRODUCTS_BASED_ON_DOES_NOT_HAVE_ATTRIBUTE,
  SELECT_PRODUCTS_BASED_ON_DOES_NOT_HAVE_TAG,
  SELECT_PRODUCTS_BASED_ON_HAVE_ATTRIBUTE,
  SELECT_PRODUCTS_BASED_ON_HAVE_TAG,
  SELECT_PRODUCTS_BASED_ON_MULTIPLE_CONDITIONS
} from "../../../../../../../../../constants/selectProductsBasedOn.js";
import MissingAttributesCard from "./components/MissingAttributesCard.js";
import SelectedTagsField from "../../../../../../../common/SelectedTagsField/SelectedTagsField.js";
import { ATTRIBUTE_TAGS } from "../../../../../../../../../constants/attributeTypes.js";
import {
  ATTRIBUTE_QUALIFIER_HAS_NOT_TAGS,
  ATTRIBUTE_QUALIFIER_HAS_TAGS
} from "../../../../../../../../../constants/attributeQualifierTypes.js";
import MultipleConditionsCard from "./components/MultipleConditionsCard/MultipleConditionsCard.js";
import { useDebounce } from "../../../../../../../../hooks/index.js";
import { useAppDispatch, useAppSelector } from "../../../../../../../../hooks/redux.js";

const getRequestObject = ({
  selectProductsBasedOn,
  specificAttributeCondition,
  missingAttributeCondition,
  selectedTags,
  multipleConditionsArray
}) => {
  if (selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_HAVE_ATTRIBUTE) {
    return { isMultiple: false, attributeCondition: specificAttributeCondition };
  }

  if (selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_DOES_NOT_HAVE_ATTRIBUTE) {
    return { isMultiple: false, attributeCondition: missingAttributeCondition };
  }

  if (selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_HAVE_TAG) {
    return {
      isMultiple: false,
      attributeCondition: { attribute: ATTRIBUTE_TAGS, qualifier: ATTRIBUTE_QUALIFIER_HAS_TAGS, value: selectedTags }
    };
  }

  if (selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_DOES_NOT_HAVE_TAG) {
    return {
      isMultiple: false,
      attributeCondition: {
        attribute: ATTRIBUTE_TAGS,
        qualifier: ATTRIBUTE_QUALIFIER_HAS_NOT_TAGS,
        value: selectedTags
      }
    };
  }

  if (selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_MULTIPLE_CONDITIONS) {
    return { isMultiple: true, multipleConditionsArray };
  }
};

function shouldSearchProducts(debouncedSearchTerm) {
  const { attributeCondition, multipleConditionsArray, isMultiple } = debouncedSearchTerm;

  const isEmptyObject = (obj) => Object.keys(obj).length === 0;

  if (isMultiple && (!multipleConditionsArray || multipleConditionsArray.length === 0 || multipleConditionsArray.every(isEmptyObject))) {
    return false;
  }

  return Object.keys(debouncedSearchTerm).length > 0 &&
    (!attributeCondition || !Array.isArray(attributeCondition.value) || attributeCondition.value.length > 0);
}

const AttributeSection = ({ values, setFormAttribute, productsCount, setProductsCount, setIsProductUsed }) => {
  const dispatch = useAppDispatch();
  const [products, setProducts] = useState([]);
  const [previewProducts, setPreviewProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [conditionTerm, setConditionTerm] = useState({});
  const debouncedSearchTerm = useDebounce(conditionTerm, 1000);
  const actionId = useAppSelector((state) => state.action.actionId);
  const initialLoad = useRef(true);

  const { mutate: mutateGetCountProducts, isLoading: isLoadingGetCountProducts } = useAppMutation({
    url: "/api/products/count/attribute/condition",
    reactMutationOptions: {
      onSuccess(data) {
        const newValue = data.productsCount - values.exceptionList.length
        setProductsCount(newValue);
      }
    }
  });

  const { mutate: mutateGetProducts, isLoading: isLoadingGetProducts } = useAppMutation({
    url: "/api/products/attribute/condition",
    reactMutationOptions: {
      onSuccess({ products }) {
        setProducts(products);
      }
    }
  });

  const { mutate: mutateGetPreviewProducts, isLoading: isLoadingGetPreviewProducts } = useAppMutation({
    url: "/api/products/attribute/condition",
    reactMutationOptions: {
      onSuccess({ products, hasActionId }) {
        setPreviewProducts(products);
        setIsProductUsed(hasActionId)
      }
    }
  });

  const getProductsCount = useCallback(() => {
    if (shouldSearchProducts(debouncedSearchTerm)) {
      mutateGetCountProducts(debouncedSearchTerm);
      setIsProductUsed(false)
      mutateGetPreviewProducts({
        ...debouncedSearchTerm,
        pageSize: 3,
        page: 1,
        actionId: actionId || undefined
      });
    } else {
      setProductsCount(0);
      setPreviewProducts([]);
    }
  }, [debouncedSearchTerm, mutateGetCountProducts, mutateGetPreviewProducts, values.actionId]);

  useEffect(() => {
    getProductsCount();
  }, [debouncedSearchTerm]);

  const updateConditionsTerms = useCallback(() => {
    setProducts(() => []);
    setProductsCount(0);
    setPreviewProducts(() => []);

    if (!initialLoad.current) {
      setFormAttribute("exceptionList", []);
    } else {
      initialLoad.current = false;
    }

    const requestObject = getRequestObject({
      selectProductsBasedOn: values.selectProductsBasedOn,
      specificAttributeCondition: values.specificAttributeCondition,
      missingAttributeCondition: values.missingAttributeCondition,
      selectedTags: values.selectedTags,
      multipleConditionsArray: values.multipleConditionsArray
    });

    setConditionTerm(requestObject);
  }, [
    values.specificAttributeCondition,
    values.missingAttributeCondition,
    values.selectProductsBasedOn,
    values.selectedTags,
    values.multipleConditionsArray
  ]);

  useEffect(() => {
    updateConditionsTerms();
  }, [
    values.specificAttributeCondition,
    values.missingAttributeCondition,
    values.selectProductsBasedOn,
    values.selectedTags,
    values.multipleConditionsArray
  ]);

  const handleLoadProducts = useCallback((page) => {
    const requestObject = getRequestObject({
      selectProductsBasedOn: values.selectProductsBasedOn,
      specificAttributeCondition: values.specificAttributeCondition,
      missingAttributeCondition: values.missingAttributeCondition,
      selectedTags: values.selectedTags,
      multipleConditionsArray: values.multipleConditionsArray
    });

    mutateGetProducts({
      ...requestObject,
      pageSize: 10,
      page
    });
  }, [
    values.specificAttributeCondition,
    values.missingAttributeCondition,
    values.selectProductsBasedOn,
    values.selectedTags,
    values.multipleConditionsArray, page
  ]);

  const handleDelete = useCallback((id) => {
    setFormAttribute("exceptionList", [...values.exceptionList, id]);
    setProductsCount(prev => {
      const newCount = prev - 1;
      return newCount;
    });
  }, [setFormAttribute, values.exceptionList]);

  const handleUndoDeletedProduct = useCallback((id) => {
    setFormAttribute("exceptionList", values.exceptionList.filter(item => item !== id));
    setProductsCount(prev => {
      const newCount = prev + 1;
      return newCount;
    });
  }, [setFormAttribute, values.exceptionList]);

  const handleCloseModal = useCallback(() => {
    setPage(1);
    setProducts([]);
    dispatch(closeModal());
  }, [setPage, setProducts]);

  const handleViewModal = useCallback(() => {
    dispatch(openModal({ modalType: OPEN_MODAL_TARGETED_PRODUCTS }));
    handleLoadProducts(1);
  }, [handleLoadProducts]);

  const handleNext = useCallback(() => {
    handleLoadProducts(page + 1)
    setPage(prev => prev + 1)
  }, [handleLoadProducts, setPage, values, page])

  const handlePrevious = useCallback(() => {
    handleLoadProducts(page - 1)
    setPage(prev => prev - 1)
  }, [mutateGetProducts, setPage, page])

  return (
    <Stack vertical>
      {
        values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_DOES_NOT_HAVE_ATTRIBUTE &&
        <Field name="missingAttributeCondition" component={MissingAttributesCard} />
      }
      {
        values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_HAVE_ATTRIBUTE &&
        <Field name="specificAttributeCondition" component={SpecificAttributeCard} isM />
      }
      {
        (values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_HAVE_TAG || values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_DOES_NOT_HAVE_TAG) &&
        <Card>
          <Card.Section>
            <Field name={"selectedTags"} component={SelectedTagsField} labelHidden={false} />
          </Card.Section>
        </Card>
      }
      {
        values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_MULTIPLE_CONDITIONS &&
        <Field name="specificAttributeCondition" component={MultipleConditionsCard} />
      }
      <CurrentSelectedProductsCard
        previewProducts={previewProducts}
        productsCount={productsCount}
        isLoading={isLoadingGetCountProducts}
        handleViewModal={handleViewModal}
        exceptionList={values.exceptionList}
      />
      <TargetedProductsModal
        isLoading={isLoadingGetProducts}
        hasNext={page * 10 < productsCount}
        hasPrevious={page > 1}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        handleDelete={handleDelete}
        products={products}
        handleCloseModal={handleCloseModal}
        exceptionList={values.exceptionList}
        handleUndoDeletedProduct={handleUndoDeletedProduct}
      />
    </Stack>
  );
};

export default AttributeSection;